import { defineStore } from 'pinia';
import { 
  getAuth,
  createUserWithEmailAndPassword,
  updatePassword,
  deleteUser as firebaseDeleteUser,
  signInWithEmailAndPassword,
  type User
} from 'firebase/auth';
import { 
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  limit,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNotificationStore } from './notificationStore';
import { initializeApp, deleteApp } from 'firebase/app';

interface UserData {
  email: string;
  password: string;
  role?: string;
}

// Firebase config for secondary app
const firebaseConfig = {
  apiKey: "AIzaSyCrBNlFBAtlE-mar_DKJrgjJxVRqbOYumo",
  authDomain: "lista-de-precios-6f44f.firebaseapp.com",
  projectId: "lista-de-precios-6f44f",
  storageBucket: "lista-de-precios-6f44f.firebaseapp.com",
  messagingSenderId: "913881951062",
  appId: "1:913881951062:web:ee7cb17ef643c6360244f7"
};

export const useUserStore = defineStore('user', () => {
  const auth = getAuth();
  const usersCollection = collection(db, 'users');
  const notificationStore = useNotificationStore();

  const createUser = async (userData: UserData) => {
    let secondaryApp;
    try {
      // Initialize a secondary Firebase app with a unique name
      const appName = 'secondary-' + Date.now();
      secondaryApp = initializeApp(firebaseConfig, appName);
      const secondaryAuth = getAuth(secondaryApp);
      
      // Create user with secondary auth
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        userData.email,
        userData.password
      );

      // Store additional user data in Firestore
      await setDoc(doc(usersCollection, userCredential.user.uid), {
        email: userData.email,
        password: userData.password, // Store password for future operations
        role: userData.role || 'user',
        createdAt: new Date().toISOString()
      });

      // Sign out from secondary auth
      await secondaryAuth.signOut();
      
      return userCredential.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    } finally {
      // Clean up secondary app
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (cleanupError) {
          console.error('Error cleaning up secondary app:', cleanupError);
        }
      }
    }
  };

  const updateUser = async (uid: string, userData: Partial<UserData>) => {
    let tempApp;
    const userRef = doc(usersCollection, uid);
    
    const updates: any = {
      role: userData.role
    };

    if (userData.password) {
      updates.password = userData.password; // Update stored password
      
      const userDoc = await getDoc(userRef);
      const userEmail = userDoc.data()?.email;
      
      if (userEmail) {
        try {
          // Create temporary app to update password
          const appName = 'temp-' + Date.now();
          tempApp = initializeApp(firebaseConfig, appName);
          const tempAuth = getAuth(tempApp);
          
          // Sign in as user
          const userCredential = await signInWithEmailAndPassword(
            tempAuth,
            userEmail,
            userData.password
          );
          
          // Update password
          await updatePassword(userCredential.user, userData.password);
          
          // Sign out
          await tempAuth.signOut();
        } catch (error) {
          console.error('Error updating password:', error);
          throw new Error('Error al actualizar la contraseña');
        } finally {
          // Clean up temp app
          if (tempApp) {
            try {
              await deleteApp(tempApp);
            } catch (cleanupError) {
              console.error('Error cleaning up temp app:', cleanupError);
            }
          }
        }
      }
    }

    await updateDoc(userRef, updates);
  };

  const deleteUser = async (uid: string) => {
    let tempApp;
    try {
      // Get user data including stored password
      const userDoc = await getDoc(doc(usersCollection, uid));
      const userData = userDoc.data();
      
      if (!userData || !userData.password) {
        throw new Error('Usuario no encontrado o datos incompletos');
      }

      try {
        // Create temporary app to delete user
        const appName = 'temp-' + Date.now();
        tempApp = initializeApp(firebaseConfig, appName);
        const tempAuth = getAuth(tempApp);
        
        // Sign in with stored credentials
        const userCredential = await signInWithEmailAndPassword(
          tempAuth,
          userData.email,
          userData.password
        );
        
        // Delete the user from Firebase Auth
        await firebaseDeleteUser(userCredential.user);
        
        // Sign out
        await tempAuth.signOut();
      } catch (authError) {
        console.error('Error deleting Firebase Auth user:', authError);
        // Continue with Firestore deletion even if Auth deletion fails
      } finally {
        // Clean up temp app
        if (tempApp) {
          try {
            await deleteApp(tempApp);
          } catch (cleanupError) {
            console.error('Error cleaning up temp app:', cleanupError);
          }
        }
      }

      // Delete from Firestore
      await deleteDoc(doc(usersCollection, uid));
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  };

  const getUsers = async () => {
    const snapshot = await getDocs(usersCollection);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        uid: doc.id,
        ...data,
        password: undefined, // Never expose password in the UI
        createdAt: data.createdAt,
        lastSignIn: data.lastSignIn || null
      };
    });
  };

  const getUserRole = async (uid: string) => {
    const userDoc = await getDoc(doc(usersCollection, uid));
    return userDoc.data()?.role || 'user';
  };

  const checkForAdminUser = async () => {
    const adminQuery = query(
      usersCollection,
      where('role', '==', 'admin'),
      limit(1)
    );
    const snapshot = await getDocs(adminQuery);
    return !snapshot.empty;
  };

  const setFirstUserAsAdmin = async (uid: string) => {
    const hasAdmin = await checkForAdminUser();
    if (!hasAdmin) {
      const userRef = doc(usersCollection, uid);
      await updateDoc(userRef, { role: 'admin' });
      return true;
    }
    return false;
  };

  return {
    createUser,
    updateUser,
    deleteUser,
    getUsers,
    getUserRole,
    checkForAdminUser,
    setFirstUserAsAdmin
  };
});